import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import API_BASE_URL from "../config/api";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIconRetina from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIconRetina,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default function LiveMap({
  vehicleId,
  liveData = null,
  onDisplayPositionChange,
  onPositionUpdate,
}) {
  const POSITION_SNAP_THRESHOLD = 0.00001;
  const POSITION_ANIMATION_DURATION = 1200;
  const [targetPosition, setTargetPosition] = useState(null);
  const [displayPosition, setDisplayPosition] = useState(null);
  const [ready, setReady] = useState(false);
  const [showNoData, setShowNoData] = useState(false);
  const animationFrameRef = useRef(null);
  const displayPositionRef = useRef(null);
  // Ref to trigger recenter from outside the map context
  const recenterRef = useRef(null);

  useEffect(() => {
    setTargetPosition(null);
    setDisplayPosition(null);
    setReady(false);
    setShowNoData(false);
  }, [vehicleId]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    displayPositionRef.current = displayPosition;
  }, [displayPosition]);

  // GPS updates come from parent Dashboard via liveData prop
  // Removed separate socket connection to avoid redundancy and connection issues

  useEffect(() => {
    if (
      !liveData ||
      liveData.latitude === undefined ||
      liveData.longitude === undefined
    ) {
      return;
    }

    const lat = parseFloat(liveData.latitude);
    const lng = parseFloat(liveData.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setShowNoData(false);
      setTargetPosition([lat, lng]);
      setReady(true);
    }
  }, [liveData]);

  useEffect(() => {
    if (!targetPosition) return;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (!displayPositionRef.current) {
      setDisplayPosition(targetPosition);
      return undefined;
    }

    const startPosition = displayPositionRef.current;
    const latDelta = targetPosition[0] - startPosition[0];
    const lngDelta = targetPosition[1] - startPosition[1];

    if (
      Math.abs(latDelta) < POSITION_SNAP_THRESHOLD &&
      Math.abs(lngDelta) < POSITION_SNAP_THRESHOLD
    ) {
      setDisplayPosition(targetPosition);
      return undefined;
    }

    const startTime = performance.now();

    const animate = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / POSITION_ANIMATION_DURATION, 1);

      setDisplayPosition([
        startPosition[0] + latDelta * progress,
        startPosition[1] + lngDelta * progress,
      ]);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [targetPosition]);

  useEffect(() => {
    if (ready) {
      setShowNoData(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setShowNoData(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [ready, vehicleId]);

  useEffect(() => {
    const currentPosition = displayPosition || targetPosition;
    if (!currentPosition) return;
    if (onDisplayPositionChange) onDisplayPositionChange(currentPosition);
    if (onPositionUpdate) onPositionUpdate(currentPosition);
  }, [displayPosition, targetPosition, onDisplayPositionChange, onPositionUpdate]);

  if (showNoData) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #ddd",
          borderRadius: "8px",
          color: "#666",
          fontSize: "16px",
        }}
      >
        No active trip for this bus
      </div>
    );
  }

  const currentPosition = displayPosition || targetPosition;

  if (!ready || !currentPosition) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #ddd",
          borderRadius: "8px",
          color: "#666",
        }}
      >
        Waiting for GPS data...
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <MapContainer
        key={`map-${vehicleId}`}
        center={currentPosition}
        zoom={13}
        zoomAnimation={false}
        markerZoomAnimation={false}
        style={{ height: "500px", width: "100%" }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <InvalidateMapSize />
        <RecenterController positionRef={recenterRef} />
        <Marker position={currentPosition}>
          <Popup>Bus Location</Popup>
        </Marker>
      </MapContainer>

      {/* Recenter button — overlaid on the map */}
      <button
        onClick={() => {
          const pos = displayPosition || targetPosition;
          if (recenterRef.current && pos) recenterRef.current(pos);
        }}
        title="Recenter on vehicle"
        style={{
          position: "absolute",
          bottom: "16px",
          right: "16px",
          zIndex: 1000,
          background: "#fff",
          border: "2px solid var(--border-dark)",
          borderRadius: "8px",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "1.1rem",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        🎯
      </button>
    </div>
  );
}

function InvalidateMapSize() {
  const map = useMap();

  useEffect(() => {
    map.invalidateSize();
  }, [map]);

  return null;
}

// Registers a recenter function into the provided ref so the button outside
// the map context can call map.setView()
function RecenterController({ positionRef }) {
  const map = useMap();

  useEffect(() => {
    positionRef.current = (position) => {
      if (position) map.setView(position, 19, { animate: true, duration: 0.8 });
    };
  }, [map, positionRef]);

  return null;
}
